interface VariationAttributeValueLite {
  value: string;
  name?: string;
}

interface VariationAttributeLite {
  id: string;
  values?: VariationAttributeValueLite[];
}

/**
 * Resolves a PDP display title from the master name + currently-selected variant attributes.
 *
 * SFCC names follow "Base, attr1[, attr2...]". When the user hasn't changed selection
 * (selected values still match loaded values), render the catalog name verbatim.
 * Otherwise strip the trailing N comma chunks (N = numAttrs) and reattach the
 * selected attribute display names. Unit-agnostic — works for oz, ct, ml, sizes,
 * colors, etc., because we never string-match inside `masterName`.
 *
 * `isTelehealth` (ET-562) always returns the master name. A treatment PDP hides
 * its variant picker, so a variant suffix there describes a selection the
 * shopper can't see or change. Regular PDPs keep the suffix because Constructor
 * hands the shopper a specific variant.
 */
export function resolveVariantTitle({
  selected,
  variationAttributes,
  variationValues,
  masterName,
  isTelehealth = false,
}: {
  selected: Record<string, string | undefined> | undefined;
  variationAttributes: VariationAttributeLite[] | undefined;
  variationValues: Record<string, string> | undefined;
  masterName: string;
  isTelehealth?: boolean;
}): string {
  if (isTelehealth) return masterName;

  const attrs = variationAttributes ?? [];
  const numAttrs = attrs.length;
  if (numAttrs === 0) return masterName;

  const loadedValues = variationValues ?? {};
  const allMatchLoaded = attrs.every(a => {
    const sel = selected?.[a.id];
    const loaded = loadedValues[a.id];
    return !!sel && !!loaded && sel === loaded;
  });
  if (allMatchLoaded) return masterName;

  const chunks = masterName.split(",");
  const baseName =
    chunks.length > numAttrs
      ? chunks.slice(0, -numAttrs).join(",").trim()
      : masterName.trim();

  const selectedNames = attrs
    .map(a => {
      const val = selected?.[a.id];
      return val ? a.values?.find(v => v.value === val)?.name : undefined;
    })
    .filter(Boolean) as string[];

  return selectedNames.length
    ? `${baseName}, ${selectedNames.join(", ")}`
    : masterName;
}
